import { Component, Input, Output, EventEmitter, OnInit, Inject, OnChanges } from 'ng-metadata/core';
import './account-menu.component.scss';
import { AppContainerStateService } from '../app-container-state.service';
import { IAppContainerState, AppContainerStateChangeName, IAppContainerCompanyState } from '../shared/shared.interfaces';

/**
 * @ngdoc directive
 * @name mwAccountMenu
 * @module mw.components.appContainer
 *
 * @param {array} [accounts] - List of accounts available to current user.
 * @param {object} [init-selected-accounts] The initially selected account to use from the list of accounts provided.
 * @param {expression} [on-select] - Angular expression to evaluate when an account is selected. `$event` is the selected account object.
 *
 * @description
 * `<mw-account-menu>` is a component used to display a accounts menu.
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-account-menu accounts="$ctrl.accounts" on-select="$ctrl.onAccountSelect($event)"></mw-account-menu>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-account-menu',
    template: `
        <md-menu class="mw-menu" ng-if="$ctrl.accounts !== undefined && $ctrl.accounts.length&gt;1" md-position-mode="target-right target" md-offset="0 50">
            <md-button aria-label="{{'Open Menu' | translate}" ng-click="$ctrl.open($mdOpenMenu, $event)" md-no-ink class="mw-menu-trigger-button">
                <md-tooltip md-direction="left" md-delay="2000" md-visible="$ctrl.isTooltipVisible">
                    {{'Switch Account' | translate:'{translateDefault: "Switch Account"}' }}
                </md-tooltip>
                <span hide-xs><span class="mw-menu-trigger-button__label mw-account-menu__trigger-button-label">{{$ctrl.selectedAccount.name}}</span></span>
                <md-icon hide-gt-xs md-svg-icon="communication:ic_business_24px"></md-icon>
                <md-icon md-svg-icon="navigation:ic_arrow_drop_down_24px"></md-icon>
            </md-button>

            <md-menu-content id="mw-account-menu-content" class="mw-menu-dropdown" md-padding="md-padding" ng-class="{'mw-menu-dropdown--has-search': $ctrl.accounts.length &gt;= 10}">
                <div ng-if="$ctrl.accounts.length &gt;= 10" class="mw-menu__input-wrapper">
                    <md-input-container class="mw-menu__input-container" md-no-float="md-no-float">
                        <md-icon md-svg-icon="action:ic_search_24px" aria-label="" class="mw-menu__input-find-icon"></md-icon>
                        <input md-autofocus class="mw-menu__input-field" type="text" translate translate-attr-placeholder="find-account--placeholder" translate-default="Find Account" aria-label="{{'Find Account' | translate}" ng-model="$ctrl.searchText"/>
                        <md-icon md-svg-icon="navigation:ic_cancel_24px" ng-show="$ctrl.searchText.length &gt;0" ng-click="$ctrl.clearSearchText()" md-prevent-menu-close="true" aria-label="{{'find-account--clear-input-text' | translate:'{translateDefault: "Empty Find"}'}}" class="mw-menu__input-find-cancel"> </md-icon>
                    </md-input-container>
                </div>
                <md-divider class="mw-menu-divider" ng-if="$ctrl.accounts.length &gt;= 10"></md-divider>
                <md-content class="mw-menu-dropdown_scrollable-content">
                    <mw-menu-item type="focusdummy" md-autofocus></mw-menu-item>
                    <mw-menu-item is-selected="account._id == $ctrl.selectedAccount._id" type="singleselect" ng-repeat="account in $ctrl.filteredAccounts = ($ctrl.accounts | filter:{name:$ctrl.searchText})" aria-label="{{account.name}}" on-click="$ctrl.setSelectedAccount(account)">{{account.name}}</mw-menu-item>
                </md-content>
                <div ng-if="$ctrl.filteredAccounts.length==0" class="mw-menu__empty-results" translate="find-account--no-results" translate-default="No results"></div>
            </md-menu-content>
        </md-menu>
    `
})
export class AccountMenuComponent implements OnInit, OnChanges {
    @Input('<') accounts: Array<Object> = [];
    @Input('<') initSelectedAccount: Object;
    @Output() onSelect = new EventEmitter<Object>();

    private deregisterMdMenuOpen: any;
    private isTooltipVisible: boolean = false;
    private searchText: string;
    public selectedAccount: Object;

    constructor(@Inject('$mdMedia') private $mdMedia: ng.material.IMedia,
                @Inject('$scope') private $scope: ng.IScope,
                @Inject('mwAppContainerStateService') private mwAppContainerStateService: AppContainerStateService) {
    }

    ngOnInit() {
        this.deregisterMdMenuOpen = this.$scope.$on('$mdMenuOpen', this.clearSearchText.bind(this));
    }

    ngOnChanges(changes) {
        if (changes.initSelectedAccount && changes.initSelectedAccount.currentValue !== null && changes.initSelectedAccount.currentValue != changes.initSelectedAccount.previousValue) {
            this.selectedAccount = this.getSelectedAccount(true);
        }
    }


    open($mdOpenMenu, $event) {
        $mdOpenMenu($event);
        this.isTooltipVisible = false;
    }

    getSelectedAccount(shouldSetStateCookie?: boolean): Object {
        let selectedAccount;

        if (this.selectedAccount && this.selectedAccount._id) {
            selectedAccount = this.selectedAccount;
        } else if (this.initSelectedAccount !== undefined && this.initSelectedAccount !== null) {
            selectedAccount = this.initSelectedAccount;
        } else if (this.accounts !== undefined && this.accounts.length > 0) {
            selectedAccount = this.accounts[0];
        } else {
            selectedAccount = {};
        }

        if (shouldSetStateCookie) {
            this.setCompanyState(selectedAccount);
        }
        return selectedAccount;
    }

    setSelectedAccount(account: object) {
        this.selectedAccount = account;
        this.setCompanyState(this.selectedAccount);
        this.clearSearchText();
        this.onSelect.emit(this.selectedAccount);
    }

    setCompanyState(company: any) {
        if (company && company.name && this.accounts) {
            let companyStateChangePayload: IAppContainerCompanyState = {
                name: company.name,
                hasMultipleCompanies: this.accounts.length > 1
            }
            
            this.mwAppContainerStateService.updateAppContainerState(AppContainerStateChangeName.company, companyStateChangePayload);
        }
    }

    getScreenSize() {
        if (this.$mdMedia('xs')) { return 'xs'; }
        if (this.$mdMedia('sm')) { return 'sm'; }
        if (this.$mdMedia('md')) { return 'md'; }
        if (this.$mdMedia('lg')) { return 'lg'; }
        if (this.$mdMedia('xl')) { return 'xl'; }
    }

    clearSearchText() {
        this.searchText = '';
    }

    $onDestroy() {
        this.deregisterMdMenuOpen();
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/app-container/account-menu/account-menu.component.ts